import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Edit, Save, X, Trash2, Upload, Loader2 } from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import React, { useState, useEffect } from "react";
import { ExtendedContract } from "@/types/payment";
import { useContract } from "@/hooks/useContract";
import { toast } from "sonner";

import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { useUser } from "@/hooks/useUser";
import { formatMileage } from "@/utils/listingUtils";
import { ContractService } from "@/services/contract/contractService";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";

interface ContractPopupProps {
  contract: ExtendedContract | null;
  onClose: () => void;
  isAdmin?: boolean;
  onUpdateContract?: (id: string, updates: Partial<{ status: string }>) => void;
}

const ContractPopup = ({
  contract,
  onClose,
  isAdmin = false,
  onUpdateContract,
}: ContractPopupProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const cp = o.contractPopup as Record<string, string>;
  const {
    updateContractAsync,
    deleteContractAsync,
    isUpdatingContract,
    isDeletingContract,
  } = useContract();

  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();
  const { userDetails } = useUser();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    contract_number: "",
  });
  const [newContractFile, setNewContractFile] = useState<{
    id: string;
    url: string;
    name: string;
  } | null>(null);
  const [originalData, setOriginalData] = useState(formData);
  const [isStartingContractSignature, setIsStartingContractSignature] = useState(false);

  useEffect(() => {
    if (contract) {
      const data = {
        contract_number: contract.contract?.contract_number || "",
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [contract]);

  if (!contract) return null;

  const signatureStatus = contract.contract?.buyer_signature_status ?? "not_requested";
  const signatureMethod = contract.contract?.buyer_signature_method ?? null;
  const isSigned = signatureStatus === "signed";
  const isDeclined = signatureStatus === "declined";
  const signerName = contract.contract?.buyer_signed_by_name ?? null;

  const signatureStatusLabel =
    signatureStatus === "signed"
      ? cp.signatureSigned ?? "Signerat"
      : signatureStatus === "requested"
        ? cp.signatureRequested ?? "Signering begärd"
        : cp.signatureNotRequested ?? "Ingen signering begärd";

  const signatureMethodLabel =
    signatureMethod === "bankid_idura"
      ? cp.methodBankId ?? "BankID"
      : signatureMethod === "physical_id_check"
        ? cp.methodPhysical ?? "Fysiskt signerad med ID-kontroll"
        : signatureMethod === "signed_externally"
          ? cp.methodExternal ?? "Signerad utanför systemet"
          : signatureMethod === "no_signature_required"
            ? cp.methodNotRequired ?? "Signering behövs ej"
            : signatureMethod === "other"
              ? cp.methodOther ?? "Annan bekräftad metod"
              : cp.methodUnknown ?? "Ej vald";

  const signatureBadgeClass =
    signatureStatus === "signed"
      ? "bg-green-100 text-green-800"
      : signatureStatus === "requested"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-800";

  const hasChanges = () => {
    return (
      formData.contract_number !== originalData.contract_number ||
      newContractFile !== null
    );
  };

  const handleSave = async () => {
    if (!contract || !hasChanges()) return;

    try {
      await updateContractAsync({
        id: contract.contract.id,
        contract_number: formData.contract_number,
        contract_file: newContractFile || contract.contract.contract_file,
      });

      setOriginalData(formData);
      setNewContractFile(null);
      setIsEditing(false);
    } catch (error) {}
  };

  const handleCancel = () => {
    setFormData(originalData);
    setNewContractFile(null);
    setIsEditing(false);
  };

  const handleDeleteContract = async () => {
    if (!contract || !window.confirm(cp.confirmDelete)) return;

    try {
      await deleteContractAsync(contract.contract.id);
      onClose();
    } catch (error) {}
  };

  const handleStartContractBankIdSignature = (contractId: string) => {
    setIsStartingContractSignature(true);
    void (async () => {
      try {
        const { isNativeCapacitorApp } = await import(
          "@/integrations/clients/carAuction/nativeAuth"
        );
        if (isNativeCapacitorApp()) {
          const continueUrl =
            await ContractService.getNativeBankIdSignatureContinueUrl(contractId);
          const { openBankIdInSystemBrowser } = await import(
            "@/lib/native/bankIdBrowser"
          );
          await openBankIdInSystemBrowser(continueUrl);
          // Browser owns the flow; clear overlay so the dialog isn't stuck.
          setIsStartingContractSignature(false);
          onClose();
          return;
        }
        window.location.href =
          ContractService.getBankIdSignatureStartUrl(contractId);
      } catch {
        setIsStartingContractSignature(false);
      }
    })();
  };
  return (
    <Dialog open={!!contract} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{cp.title}</DialogTitle>
            {isAdmin && (
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {cp.edit}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      {cp.cancel}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteContract}
                      disabled={isDeletingContract}
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeletingContract ? cp.deleting : cp.delete}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[68vh] pr-4">
          <div className="space-y-6 p-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {cp.contractNumber}
                </Label>
                {isEditing ? (
                  <Input
                    value={formData.contract_number}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contract_number: e.target.value,
                      }))
                    }
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 text-sm">
                    {contract.contract?.contract_number || o.invoicePopup.nA}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {cp.signatureStatus ?? "Signeringsstatus"}
                </Label>
                <div className="mt-2">
                  <Badge className={isDeclined ? "bg-red-100 text-red-800" : signatureBadgeClass}>
                    {isDeclined
                      ? cp.signatureDeclined ?? "Signering misslyckad"
                      : signatureStatusLabel}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {cp.signatureMethod ?? "Metod"}
                </Label>
                <p className="mt-1 text-sm">{signatureMethodLabel}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {signatureStatus === "signed"
                    ? cp.signatureDate ?? "Signeringsdatum"
                    : cp.signatureRequestedDate ?? "Begärd"}
                </Label>
                <p className="mt-1 text-sm">
                  {signatureStatus === "signed"
                    ? (contract.contract?.buyer_signed_at
                        ? new Date(contract.contract.buyer_signed_at).toLocaleString("sv-SE")
                        : o.invoicePopup.nA)
                    : (contract.contract?.buyer_signature_requested_at
                        ? new Date(contract.contract.buyer_signature_requested_at).toLocaleString("sv-SE")
                        : o.invoicePopup.nA)}
                </p>
              </div>
            </div>
            {signerName && (
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {cp.signatureSignedBy ?? "Signerad av"}
                </Label>
                <p className="mt-1 text-sm">{signerName}</p>
              </div>
            )}
            {contract.contract?.buyer_signature_note && (
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {cp.signatureNote ?? "Notering"}
                </Label>
                <p className="mt-1 text-sm">{contract.contract.buyer_signature_note}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="border rounded p-3 bg-muted">
                <div className="font-semibold mb-1">{cp.customerInfo}</div>
                {contract.customer ? (
                  <>
                    <div>
                      {cp.name}: {contract.customer.firstName}{" "}
                      {contract.customer.lastName}
                    </div>
                    <div>
                      {cp.email}: {contract.customer.email}
                    </div>
                    <div>
                      {cp.userType}: {contract.customer.role}
                    </div>
                  </>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {cp.noCustomerInfo}
                  </div>
                )}
              </div>
              <div className="border rounded p-3 bg-muted">
                <div className="font-semibold mb-1">{cp.vehicle}</div>
                {contract.listing ? (
                  <>
                    <div>
                      {cp.regNumber}: {contract.listing.regNumber}
                    </div>
                    <div>
                      {cp.make}: {contract.listing.make}
                    </div>
                    <div>
                      {cp.model}: {contract.listing.model}
                    </div>
                    <div>
                      {cp.year}: {contract.listing.year}
                    </div>
                    <div>
                      {cp.color}: {contract.listing.color}
                    </div>
                    <div>
                      {cp.mileage}: {formatMileage(contract.listing.mileage)}{" "}
                      {cp.miles}
                    </div>
                  </>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {cp.noVehicleInfo}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  {cp.documents}
                </h3>
                {isEditing && (
                  <FileUploadTrigger
                    accept="application/pdf,image/*"
                    onFilesSelected={(files) => {
                      const file = files[0];
                      if (!file) return;
                      const mockFileData = {
                        id: `file_${Date.now()}`,
                        url: URL.createObjectURL(file),
                        name: file.name,
                      };
                      setNewContractFile(mockFileData);
                    }}
                  >
                    {(open) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={open}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        {cp.uploadNewFile}
                      </Button>
                    )}
                  </FileUploadTrigger>
                )}
              </div>
              <div className="space-y-2">
                {newContractFile && (
                  <div className="flex items-center justify-between p-2 rounded-lg border bg-blue-50">
                    <span className="text-sm text-blue-700">
                      {cp.newFileSelected}: {newContractFile.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setNewContractFile(null)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {contract.contract?.contract_file && !newContractFile && (
                  <div className="flex items-center justify-between gap-3 p-2 rounded-lg border">
                    <span className="text-sm min-w-0 flex-1 break-words">
                      {cp.contractFile} ({contract.contract.contract_file.name})
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          if (contract.contract?.contract_file) {
                            try {
                              // If mediaId exists, fetch presigned URL on-demand via hook
                              let downloadUrl =
                                contract.contract.contract_file.url;
                              if (
                                contract.contract.contract_file.mediaId &&
                                !contract.contract.contract_file.url
                              ) {
                                downloadUrl = await getDownloadUrlAsync({
                                  mediaId:
                                    contract.contract.contract_file.mediaId,
                                  expiresIn: 3600,
                                });
                              }
                              const filename = await downloadFile(
                                downloadUrl,
                                contract.contract.contract_file.name,
                              );
                              toast.success(o.invoicePopup.downloadStarted, {
                                description: o.invoicePopup.downloadDescription.replace(
                                    "{filename}",
                                    filename,
                                  ),
                              });
                            } catch (error) {
                              const errorMessage =
                                error instanceof Error
                                  ? error.message
                                  : o.invoicePopup.unknownError;
                              toast.error(o.invoicePopup.downloadError, { description: `${contract.contract.contract_file.name}\n${o.invoicePopup.errorLabel}: ${errorMessage}` });
                            }
                          }
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {isEditing && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleDeleteContract}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="flex flex-col items-end gap-2 mt-6 pt-4 border-t">
          {!isAdmin &&
            !isSigned &&
            userDetails?.bankIdStatus !== "approved" &&
            Boolean(contract.contract?.contract_file) && (
              <p className="w-full text-sm text-muted-foreground text-left">
                {cp.signWithoutBankIdHint ??
                  "Har du inte BankID? Kontakta Bilenia så hjälper vi dig signera kontraktet manuellt."}
              </p>
            )}
          <div className="flex justify-end gap-2 w-full">
          {!isAdmin &&
            !isSigned &&
            userDetails?.bankIdStatus === "approved" &&
            Boolean(contract.contract?.contract_file) && (
              <Button
                className="gap-2"
                onClick={() => {
                  handleStartContractBankIdSignature(contract.contract.id);
                }}
              >
                <img
                  src="/assets/BankID_logo.png"
                  alt="BankID"
                  className="h-4 w-auto object-contain"
                />
                {cp.signWithBankId ?? "Signera med BankID"}
              </Button>
            )}
          {isAdmin && isEditing && (
            <Button
              variant="default"
              className="gap-2"
              onClick={handleSave}
              disabled={!hasChanges() || isUpdatingContract}
            >
              <Save className="h-4 w-4" />
              {isUpdatingContract ? o.invoicePopup.saving : cp.saveChanges}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {cp.close}
          </Button>
          </div>
        </div>
        {isStartingContractSignature && (
          <div className="absolute inset-0 z-[130] bg-background/90 backdrop-blur-sm flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 rounded-2xl border bg-background px-8 py-7 shadow-xl">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="text-center">
                <div className="text-base font-semibold">
                  {cp.redirectingToBankId ?? "Öppnar BankID-signering"}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {cp.redirectingToBankIdDescription ??
                    "Vänta medan vi skickar dig vidare till Idura för att signera kontraktet."}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractPopup;
